import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { TASK_CONST } from '@/constant';
import { useTaskCreateMutation } from '@/hook';
import { useUnleash } from '@/package';
import { useCollectionManagementStore } from '@/store';
import type { Project, TaskApi } from '@/type';

import type { TaskCreateFormType } from './type';

export const useFormDefaultValue = ({
  defaultValue,
  project,
}: {
  defaultValue?: TaskCreateFormType;
  project?: Project;
}) => {
  const createFormDefaultValue = useMemo(() => {
    const extendClass = project?.extendClass.filter(item => item.enable) ?? [];
    const classConfidence = extendClass.map(item => ({
      id: item.id,
      confidence: item.confidence.toFixed(3),
      classType: item.classType,
    }));

    /**
     * FIXME: unknown 쓰지 않고 타입 맞춰야함
     */
    return {
      workFrameInterval: project?.defaultInterval,
      basePath: '',
      orgPath: '',
      taskName: '',
      startDate: dayjs(new Date()).format('YYYY-MM-DD'),
      dueDate: dayjs(new Date()).format('YYYY-MM-DD'),
      extendClass,
      classConfidence,
      distribution: {
        distributions: [],
        method: TASK_CONST.TASK_DISTRIBUTE_METHOD.RATIO,
      },
      worker: project?.user,
    } as unknown as TaskCreateFormType;
  }, [project]);

  return defaultValue ?? createFormDefaultValue;
};

export const useTaskCreateForm = ({
  formDefaultValue,
  onTaskCreated,
}: {
  formDefaultValue: TaskCreateFormType;
  onTaskCreated?: () => void;
}) => {
  const { projectId } = useParams();
  const { getVariantPayloadValue } = useUnleash();
  const { mutate: createTask } = useTaskCreateMutation();
  const { currentBucket } = useCollectionManagementStore();
  const distanceConfig = getVariantPayloadValue(
    'vuex.sapporo.defaultDistanceValue',
  ) as {
    default: number;
    max: number;
    min: number;
  };
  const methods = useForm<TaskCreateFormType>({
    defaultValues: formDefaultValue,
    mode: 'onChange',
  });
  const { handleSubmit, reset } = methods;

  const handleCreateTask = () => {
    handleSubmit(data => {
      const { totalTaskRatio: _, ...rest } = data;

      /**
       * FIX: unknown 쓰지 않고 타입 맞춰야함
       */
      createTask(
        {
          ...rest,
          bucket: currentBucket!,
          projectID: Number(projectId!),
          pProjectID: Number(projectId!),
          orgPath: data.orgPath,
          basePath: data.basePath,
          startDate: data.startDate ? `${data.startDate}T09:00:00Z` : '',
          dueDate: data.dueDate ? `${data.dueDate}T09:00:00Z` : '',
          extendClass:
            data.extendClass?.map(item => ({
              id: item.id,
            })) ?? [],
          classConfidence: {
            confidence: data.classConfidence
              .filter(item =>
                data.extendClass?.some(
                  extendClass => extendClass.id === item.id,
                ),
              )
              .map(item => ({
                id: item.id,
                confidence: Number(item.confidence),
                classType: item.classType,
              })),
          },
          distance: +(data.distance ?? distanceConfig.default),
          workFrameInterval: Number(data.workFrameInterval ?? 1),
          worker: data.worker?.map(user => ({ id: user.id })),
        } as unknown as TaskApi.Create.Request,
        {
          onSuccess: () => {
            onTaskCreated?.();
            reset();
          },
        },
      );
    })();
  };

  return {
    handleCreateTask,
    methods,
  };
};
